import type { IOpenTransactionDetailsProps } from "@/modules/transaction/common/types";
import {
  AccountTransferDetails,
  BudgetTransferDetails,
  CardTransferDetails,
  CurrencyExchangeDetails,
  GroupTransferDetails,
  LoanRepaymentDetails,
  OwnAccountTransferDetails,
  TransactionHistory,
} from "@/modules/transaction/components";
import { setRowId, setTransaction } from "@/modules/transaction/state";
import { PageHeader } from "@/shared/components";
import { useAppDispatch } from "@/shared/hooks";
import { setActiveTransactionId, setActiveTransactionType } from "@/shared/state/common";
import { useState } from "react";

interface IDetailsOpenState {
  isAccountTransfer: boolean;
  isOwnAccountTransfer: boolean;
  isCardTransfer: boolean;
  isCurrencyExchange: boolean;
  isBudgetTransfer: boolean;
  isGroupTransfer: boolean;
  isLoanRepayment: boolean;
}

export const TransactionHistoryPage = () => {
  const dispatch = useAppDispatch();
  const [detailsOpenState, setDetailsOpenState] = useState<IDetailsOpenState>({
    isAccountTransfer: false,
    isOwnAccountTransfer: false,
    isCardTransfer: false,
    isCurrencyExchange: false,
    isBudgetTransfer: false,
    isGroupTransfer: false,
    isLoanRepayment: false,
  });
  const [transactionId, setTransactionId] = useState<string | null>(null);

  const onOpenDetails = ({ id, transactionType }: IOpenTransactionDetailsProps) => {
    const key = `is${transactionType}` as keyof IDetailsOpenState;
    if (key in detailsOpenState) {
      setDetailsOpenState((prev) => {
        const newState = {} as IDetailsOpenState;
        const stateKeys = Object.keys(prev) as (keyof IDetailsOpenState)[];
        stateKeys.forEach((k) => {
          newState[k] = k === key;
        });
        return newState;
      });
      setTransactionId(id as string);
    } else {
      dispatch(setRowId(null));
      dispatch(setTransaction(null));
    }
  };

  const resetDetailsState = (state: IDetailsOpenState): IDetailsOpenState => {
    return Object.keys(state).reduce((acc, key) => {
      acc[key as keyof IDetailsOpenState] = false;
      return acc;
    }, {} as IDetailsOpenState);
  };

  const onCloseDetails = () => {
    setDetailsOpenState(prev => resetDetailsState(prev));
    setTransactionId(null);
    dispatch(setActiveTransactionId(null));
    dispatch(setActiveTransactionType(null));
    dispatch(setRowId(null));
    dispatch(setTransaction(null));
  };

  return (
    <>
      <div className="full-width page-container transaction-history-page">
        <PageHeader/>
        <TransactionHistory openDetails={onOpenDetails} closeDetails={onCloseDetails}/>
      </div>
      {transactionId && detailsOpenState.isAccountTransfer &&
        <AccountTransferDetails
          onClose={onCloseDetails}
          isOpen={detailsOpenState.isAccountTransfer}
          transactionId={transactionId}
        />
      }
      {transactionId && detailsOpenState.isOwnAccountTransfer &&
        <OwnAccountTransferDetails
          onClose={onCloseDetails}
          isOpen={detailsOpenState.isOwnAccountTransfer}
          transactionId={transactionId}
        />
      }
      {transactionId && detailsOpenState.isCardTransfer &&
        <CardTransferDetails
          onClose={onCloseDetails}
          isOpen={detailsOpenState.isCardTransfer}
          transactionId={transactionId}
        />
      }
      {transactionId && detailsOpenState.isCurrencyExchange &&
        <CurrencyExchangeDetails
          onClose={onCloseDetails}
          isOpen={detailsOpenState.isCurrencyExchange}
          transactionId={transactionId}
        />
      }
      {transactionId && detailsOpenState.isBudgetTransfer &&
        <BudgetTransferDetails
          onClose={onCloseDetails}
          isOpen={detailsOpenState.isBudgetTransfer}
          transactionId={transactionId}
        />
      }
      {transactionId && detailsOpenState.isGroupTransfer &&
        <GroupTransferDetails
          onClose={onCloseDetails}
          isOpen={detailsOpenState.isGroupTransfer}
          transactionId={transactionId}
        />
      }
      {transactionId && detailsOpenState.isLoanRepayment &&
        <LoanRepaymentDetails
          onClose={onCloseDetails}
          isOpen={detailsOpenState.isLoanRepayment}
          transactionId={transactionId}
        />
      }
    </>
  );
};
